<template>
  <component :is="'style'">
    {{
      css`
        @media print {
          table {
            page-break-after: always;
          }

          * {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
        body {
          margin: 0;
          font-family: sans-serif;
        }

        .printAgenda {
          background-color: #ffffff;
          padding: 0 24px;
        }
        .printAgenda section {
          margin-top: 2rem;
          padding: 1.5rem;
          background-color: #ffffff;
          border: 1px solid #eadfd8;
          border-radius: 10px;
        }
        .printAgenda section:first-child {
          margin-top: 1rem;
        }

        table {
          width: 100%;
          margin-bottom: 1rem;
          color: #372929;
          vertical-align: top;
          border-collapse: collapse;
          border-top: 1px solid #eadfd8;
        }

        thead tr th {
          padding: 10px;
          text-align: center;
          border-bottom: 1px solid #eadfd8;
          border-left: 1px solid #eadfd8;
        }

        thead tr th:first-child {
          border-left: 1px solid #eadfd8;
        }

        thead tr th:last-child {
          border-right: 1px solid #eadfd8;
        }

        tbody tr {
          border-width: 0;
        }

        tbody tr:not(:last-child) td {
          border-bottom: 1px solid #eadfd8;
        }
        tbody tr:last-child td {
          border-bottom: 1px solid #eadfd8;
        }
        tbody td {
          padding: 4px;
          text-align: center;
          border-left: 1px solid #eadfd8;
        }

        tbody td:first-child {
          font-size: 0.875rem;
          font-family: monospace, 'Roboto', sans-serif;
        }

        tbody td:last-child {
          border-right: 1px solid #eadfd8;
        }
          
        footer {
          display: flex;
          justify-content: space-between;
          border-top: 1px solid #d1cec6 !important;
          align-items: center;
        }
        .logo {
          width: auto;
          height: 2rem;
        }

        .logo svg {
          height: 100%;
          width: auto;
        }

        .conference-title {
          margin-bottom: 10px;
        }

        .qr-code-wrap {
          display: flex;
          justify-content: flex-end;
        }
      `
    }}
  </component>
  <div class="printAgenda">
    <typography weight="500" size="1.5rem">
      Agenda for
      {{ conference.conference_name }},
      {{
        conference.team?.team_name || `${authStore.me.teacher_lname}, ${authStore.me.teacher_fname}`
      }}
    </typography>
    <section v-for="eventDay in agendas" :key="eventDay.conferenceDay.conference_days_id">
      <Typography tag="div" weight="600" size="1.25rem" class="conference-title">
        {{ dayjs(eventDay.conferenceDay.start).format("dddd, MMMM D, YYYY") }}
      </Typography>
      <div
        v-if="
          eventDay.conferenceDay.virtual_room &&
          eventDay.conferenceDay.meeting_types?.virtual &&
          eventDay.conferenceDay.teacher_meeting_types?.virtual
        "
        style="
          margin-bottom: 5px;
          display: flex;
          justify-content: space-between;
          flex-direction: row-reverse;
          gap: 10px;
        "
      >
        <a :href="eventDay.conferenceDay.virtual_room" class="qr-code-wrap">
          <div :id="`qr-code-${eventDay.conferenceDay.conference_days_id}`" class="qr-code"></div>
        </a>
        <div>
          <div>virtual link:</div>
          <a style="word-break: break-all" :href="eventDay.conferenceDay.virtual_room">{{
            eventDay.conferenceDay.virtual_room
          }}</a>
        </div>
      </div>
      <div
        v-if="
          eventDay.conferenceDay.meeting_types?.in_person &&
          eventDay.conferenceDay.teacher_meeting_types?.in_person
        "
        style="margin-bottom: 25px"
      >
        <span>room: </span>
        <span>{{
          eventDay.conferenceDay.room || authStore.me.room_assignment || "Unavailable"
        }}</span>
      </div>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th scope="col">Date/Time</th>
            <th scope="col">Child</th>
            <th scope="col">Parent</th>
            <th scope="col">Location</th>
            <th v-if="confStore.selected?.request_translator" scope="col">Translator</th>
            <th v-if="showComments" scope="col">Comment</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="appointment in eventDay.agenda" :key="appointment.pta_id || appointment.timeslot">
            <td class="text-nowrap commaBlock" data-col="Date/Time: ">
              {{ dayjs(appointment.timeslot).format("MMM Do, h:mm A") }}
            </td>
            <template v-if="appointment.flag === 'R'">
              <td data-col="Child: ">{{ appointment.child_name }}</td>
              <td data-col="Meeting: ">{{ appointment.parent_name }}</td>
              <td data-col="Meeting Type/Location: ">
                <span
                  class="d-flex align-items-center"
                  v-if="appointment.meeting_type == 'in_person'"
                >
                <v-icon>mdi-office-building-marker</v-icon> {{ appointment.room ? appointment.room : "Room not set yet" }}
                </span>
                <span
                  class="d-flex align-items-center"
                  v-else-if="appointment.meeting_type == 'virtual'"
                >
                <v-icon>mdi-monitor-account</v-icon>  {{ appointment.virtual_room ? appointment.virtual_room : "Missing link"}}
                </span>
                <span
                  class="d-flex align-items-center"
                  v-else-if="appointment.meeting_type == 'callback'"
                >
                <v-icon>mdi-phone-classic</v-icon>{{ appointment.callback_number || appointment.phone || "Missing callback number" }}
                </span>
              </td>
            </template>
            <template v-else-if="appointment.flag === 'O' || appointment.flag === 'T'">
              <Typography tag="td" color="gray800" colspan="2" align="start">
                <typography color="primary400" weight="600" transform="capitalize">{{
                  appointment.flag === "O" ? "Team Teacher conflict" : "Team Conflict"
                }}</typography>
                {{ appointment.flag === "T" ? appointment.team_name : "One of the teachers has a meeting with"}}
                <typography color="primary400" weight="600" transform="capitalize">Parent:</typography>  {{ appointment.parent_name }}
                <typography color="primary400" weight="600" transform="capitalize">Child:</typography> {{ appointment.child_name }}
                &nbsb;
                {{ appointment.child_description }}
              </Typography>
              <td data-col="Meeting Type/Location: "></td>
            </template>
            <template v-else-if="appointment.flag === 'N'">
              <td data-col="Child: " colspan="3" style="text-align: center;">
                <typography color="secondary" weight="600" transform="uppercase">Break/Unavailable</typography>
              </td>
            </template>
            <template v-else-if="!appointment.pta_id">
              <!-- Empty timeslot (not booked, not a break) -->
              <td data-col="Child: "></td>
              <td data-col="Meeting: "></td>
              <td data-col="Meeting Type/Location: "></td>
            </template>
            <template v-else>
              <!-- Fallback for any other cases -->
              <td data-col="Child: ">{{ appointment.child_name || '' }}</td>
              <td data-col="Meeting: ">{{ appointment.parent_name || '' }}</td>
              <td data-col="Meeting Type/Location: ">
                <span
                  class="d-flex align-items-center"
                  v-if="appointment.meeting_type == 'in_person'"
                >
                <v-icon>mdi-office-building-marker</v-icon> {{ appointment.room ? appointment.room : "Room not set yet" }}
                </span>
                <span
                  class="d-flex align-items-center"
                  v-else-if="appointment.meeting_type == 'virtual'"
                >
                <v-icon>mdi-monitor-account</v-icon>  {{ appointment.virtual_room ? appointment.virtual_room : "Missing link"}}
                </span>
                <span
                  class="d-flex align-items-center"
                  v-else-if="appointment.meeting_type == 'callback'"
                >
                <v-icon>mdi-phone-classic</v-icon>{{ appointment.callback_number || appointment.phone || "Missing callback number" }}
                </span>
              </td>
            </template>
            <td v-if="showComments" data-col="Comment">
              {{ appointment.comment }}
            </td>
            <td v-if="confStore.selected?.request_translator" data-col="Translator">
              {{ getLanguageName(appointment.translator_language) }}
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <footer>
      <!-- <p>Generated at 12:00 AM on Tue, Jan 01, 2022 by PTC Wizard</p> -->
      <p>Generated at {{ dayjs().format("h:mm A on ddd, MMM Do, YYYY") }} by PTC Wizard</p>
      <figure class="logo" alt="PTC Wizard Logo">
        <Logo />
      </figure>
    </footer>
  </div>
</template>

<script setup lang="ts">
import type { ConferenceDay, GetConference, GetSchedule } from "@/api_types/teacher";
import { useAuthenticationStore } from "@/stores/authentication";
import { useConferenceStore } from "@/stores/teacher/conference";
import dayjs from "dayjs";
import { onMounted } from "vue";
import { computed } from "vue";
import Logo from "@/assets/icons/logo-vertical-regular.svg";
import Typography from "@/components/typography.vue";
import { getLanguageName } from "@/composables/useLanguage.js";

import advancedFormat from "dayjs/esm/plugin/advancedFormat";

// @ts-ignore
dayjs.extend(advancedFormat);
const props = defineProps<{
  agendas: { agenda: GetSchedule[]; conferenceDay: ConferenceDay }[];
  conference: GetConference;
  win: Window;
}>();

const confStore = useConferenceStore();
const authStore = useAuthenticationStore();

const css = (s: TemplateStringsArray, ...f: string[]): string => {
  return s.map((s, i) => s + (f[i] || "")).join("");
};

const agendaDays = computed(() => props.agendas.map((a) => a));
// eslint-disable-next-line
const showComments = computed(() => confStore.selected?.allow_comments);

// eslint-disable-next-line
const parseMeetingType = (type) => {
  if (type === "in_person") return "In Person";
  return type || "Not Available";
};

onMounted(() => {
  for (const day of agendaDays.value) {
    if (day.conferenceDay.virtual_room) {
      const qrId = "qr-code-" + day.conferenceDay.conference_days_id;
      // @ts-ignore
      if (window.QRCode)
        try {
          // @ts-ignore
          new window.QRCode(props.win.document.getElementById(qrId), {
            text: day.conferenceDay.virtual_room,
            height: 64,
            width: 64
          });
        } catch (error) {
          //
        }
    }
  }
});
</script>
<style>
body {
  font-family: sans-serif;
}
</style>
